import type { AnswerMap, SampleEntryValue } from './template.js';

/**
 * AuditResponses rows → the answers of one audit. Pure, shared: the app
 * (record page, cross-device resume) and the server (the audit PDF) read
 * answers the same way.
 */

export interface ResponseRow {
  itemId: number;
  questionId: string;
  sampleIndex: number | null;
  valueJson: string;
}

/**
 * Highest sample index read back (no template samples near it). The column
 * is conductor-written: a forged 2,000,000,000 must not build a list that
 * long (the server renders PDFs from these rows).
 */
export const MAX_SAMPLE_INDEX = 999;

/**
 * Rebuild local session state from synced server rows (cross-device resume,
 * the read-only record, the PDF). Unreadable rows are skipped.
 */
export function buildAnswersFromRows(rows: readonly ResponseRow[]): {
  answers: AnswerMap;
  responseIds: Record<string, number>;
} {
  const answers: AnswerMap = {};
  const responseIds: Record<string, number> = {};
  const groups = new Map<string, Map<number, SampleEntryValue>>();

  for (const row of rows) {
    if (!row.questionId) continue;
    if (row.sampleIndex !== null && !(Number.isInteger(row.sampleIndex) && row.sampleIndex >= 0 && row.sampleIndex <= MAX_SAMPLE_INDEX)) continue;
    let value: unknown;
    try {
      value = JSON.parse(row.valueJson);
    } catch {
      continue; // Unreadable row — skip rather than corrupt the session.
    }
    responseIds[`${row.questionId}#${row.sampleIndex ?? ''}`] = row.itemId;

    if (row.sampleIndex !== null) {
      const entries = groups.get(row.questionId) ?? new Map<number, SampleEntryValue>();
      entries.set(row.sampleIndex, (value ?? {}) as SampleEntryValue);
      groups.set(row.questionId, entries);
    } else if (value !== null && value !== undefined) {
      answers[row.questionId] = value as AnswerMap[string];
    }
  }

  for (const [questionId, entries] of groups) {
    const maxIndex = Math.max(...entries.keys());
    const list: SampleEntryValue[] = [];
    for (let i = 0; i <= maxIndex; i++) {
      list.push(entries.get(i) ?? {});
    }
    answers[questionId] = list;
  }

  return { answers, responseIds };
}
